import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { isDayString } from "../../../../../app/src/Utils/agendaDateUtils";

// Estado único de la Agenda en la URL (OTT-2011 Fase 2, pantalla 15): filtros (`sport`) + día
// abierto (`dia`). Mismo patrón que el buscador (searchUrlState.js): la URL es la única fuente de
// verdad, history.push en cada cambio, y el atrás/adelante del navegador (popstate) relanza la
// consulta porque react-router re-renderiza con la nueva `location`.
//
// URL: /agenda?sport=futbol,balonmano[&dia=2026-10-03]. `sport` lleva slugs de nodos type=agenda
// (mapa slug ↔ id de useFilterNodes); `dia` es lo único que cambia entre lista y vista de día.
// La casilla marcada por scroll (scroll-spy) NO va en la URL: es estado local de la página.
//
// A diferencia del buscador (que usa `nodes=`), aquí se mantiene el `sport=` de la especificación:
// la Agenda solo existe para tiivii y sus filtros son deportes (supuestos 17).

const PARAM = { SPORT: "sport", DIA: "dia" };

/** @returns {{sportSlugs: string[], dia: string}} `dia` vacío si falta o no es YYYY-MM-DD. */
export const parseAgendaUrlState = (search) => {
  const params = new URLSearchParams(search);
  const sport = params.get(PARAM.SPORT);
  const dia = params.get(PARAM.DIA);
  return {
    sportSlugs: sport ? sport.split(",").filter(Boolean) : [],
    dia: isDayString(dia) ? dia : "",
  };
};

export const serializeAgendaUrlState = (state) => {
  const params = new URLSearchParams();
  if (state.sportSlugs?.length) params.set(PARAM.SPORT, state.sportSlugs.join(","));
  if (state.dia) params.set(PARAM.DIA, state.dia);
  // URLSearchParams codifica las comas (%2C): se dejan legibles, como en la URL del diseño.
  return params.toString().replace(/%2C/g, ",");
};

/**
 * Lee/escribe el estado de la Agenda en la URL de la página actual.
 * @returns {{state: {sportSlugs: string[], dia: string}, setSportSlugs: Function, openDay: Function,
 *   closeDay: Function, clearFilters: Function, ignoreDay: Function}}
 */
export const useAgendaUrlState = () => {
  const history = useHistory();
  const location = useLocation();

  const state = useMemo(() => parseAgendaUrlState(location.search), [location.search]);

  const navigate = useCallback(
    (patch, { replace = false } = {}) => {
      const search = serializeAgendaUrlState({ ...state, ...patch });
      const target = { pathname: location.pathname, search: search ? `?${search}` : "" };
      if (replace) {
        history.replace(target);
      } else {
        history.push(target);
      }
    },
    [state, history, location.pathname],
  );

  // "Aplicar filtros", chip × y "Limpiar": cambian los filtros y se conservan en lista y en día.
  const setSportSlugs = useCallback((sportSlugs) => navigate({ sportSlugs }), [navigate]);
  // "Ver más ›" o casilla de la tira dentro de la vista de día.
  const openDay = useCallback((dia) => navigate({ dia }), [navigate]);
  // "‹ Volver a todas las fechas" (atrás del navegador hace lo mismo sin pasar por aquí).
  const closeDay = useCallback(() => navigate({ dia: "" }), [navigate]);
  // "Ver toda la agenda" (06): quita todos los filtros y vuelve a la lista.
  const clearFilters = useCallback(() => navigate({ sportSlugs: [], dia: "" }), [navigate]);
  // `dia` anterior a hoy, posterior a meta.until o sin eventos: se ignora y se abre la lista.
  // Con replace, para que atrás no vuelva a una URL que no lleva a ningún sitio.
  const ignoreDay = useCallback(() => navigate({ dia: "" }, { replace: true }), [navigate]);
  // Filtros de la URL que ya no existen (slug desconocido o 404 node-not-found): se quitan sin
  // dejar entrada en el historial.
  const replaceSportSlugs = useCallback((sportSlugs) => navigate({ sportSlugs }, { replace: true }), [navigate]);

  return { state, setSportSlugs, openDay, closeDay, clearFilters, ignoreDay, replaceSportSlugs };
};
